import type es from '../es/screening';

/** `screening` in English. Same keys as `es/screening.ts`, enforced by the type. */
const screening: typeof es = {
  steps: {
    basics: {
      title: "Let's start with you",
      lede: 'This is how employers will reach you.',
    },
    target: {
      title: 'What work you want',
      lede: 'We use this to find the jobs that fit you.',
    },
    experience: {
      title: "Where you've worked",
      lede: 'Say it out loud: it comes out better than writing, and it goes straight into your resume.',
    },
    skills: {
      title: 'What you can do',
      lede: 'The machines you run and the languages you speak count a lot.',
    },
    availability: {
      title: 'When you can start',
      lede: 'Most of these jobs are seasonal and start on a set date.',
    },
  },

  options: {
    visa: {
      h2b: {
        label: 'H-2B: temporary work',
        hint: 'Hospitality, construction, cleaning, kitchen, landscaping.',
      },
      h2a_jo: {
        label: 'H-2A: farm work',
        hint: 'Harvesting, packing, greenhouses, livestock.',
      },
    },
    workArea: {
      agricultura: 'Agriculture',
      paisajismo: 'Gardening and landscaping',
      construccion: 'Construction',
      hoteleria: 'Hospitality',
      limpieza: 'Cleaning',
      cocina: 'Kitchen',
      bodega: 'Warehouse',
      otro: 'Other',
    },
    languageLevel: {
      basic: 'Basic',
      conversational: 'I get by',
      fluent: 'Fluent',
    },
    startWhen: {
      now: 'Right away',
      month: 'In a month',
      contract: 'The contract date',
      unsure: "I'm not sure",
    },
    physicalChips: {
      lift: 'I can lift 50 pounds or more',
      standing: 'I can handle long shifts on my feet',
      heat: 'I work well in hot weather',
      early: "I'm fine with early mornings",
    },
  },

  errors: {
    fullName: 'Enter your full name.',
    cityCountry: 'Tell us the city and country where you live.',
    phoneIncomplete: 'Check the number: pick your country and type it without the country code.',
    maxChars: (n: number) => `${n} characters max.`,
    maxItems: (n: number) => `${n} max.`,
    pickVisa: 'Pick at least one.',
    pickArea: 'Pick at least one area.',
    yearsNumber: "Enter the number of years. If it's your first time, enter 0.",
    yearsRange: 'Enter a number between 0 and 60.',
    company: 'Add the name of the company or farm.',
    role: 'Add the job title you had.',
    duties: 'Tell us what you did there: talk or type.',
    startYear: (min: number, max: number) =>
      `Enter the year you started, between ${min} and ${max}.`,
    endYear: "Enter the year you left, or mark that you're still there.",
    endBeforeStart: "The year you left can't be before the year you started.",
    education: 'Tell us how far you got in school.',
    skillLength: 'A few words each, 80 characters max.',
    language: 'Enter the language or remove the row.',
    relocate: 'Pick one of the two.',
    startWhen: 'Tell us when you can start.',
  },

  issues: {
    lastJobs: "Check this job's details.",
    skills: 'Check this list.',
    languages: 'Check this language.',
    availability: 'Check your availability.',
    fallback: 'Check this field.',
  },

  wizard: {
    stepOf: (n: number, total: number) => `Step ${n} of ${total}`,
    progressLabel: 'Questionnaire progress',
    cancel: 'Cancel',
    back: 'Back',
    next: 'Next',
    create: 'Create my resume',
    createNote:
      "We write it in English: that's the language US employers read it in. You'll be able to review it before you use it.",
    optional: ' (optional)',
  },

  basics: {
    fullName: 'Full name',
    fullNamePlaceholder: 'As it appears on your passport',
    cityCountry: 'City and country where you live',
    cityCountryHint: 'Example: Cali, Colombia',
    phone: 'Your WhatsApp',
    phoneHint: 'We message you on WhatsApp about your applications and if an employer wants to assess your English.',
  },

  target: {
    visa: 'Which visa do you want to apply for?',
    visaHint: 'You can pick both.',
    areas: 'What have you worked in, or want to work in?',
    areasHint: 'Pick all that apply.',
    years: 'How many years have you been working?',
    yearsHint: "In total, counting all your jobs. If it's your first time, enter 0.",
  },

  experience: {
    intro:
      'Tell us about up to three jobs, starting with the most recent. Farm work, day labor and seasonal work count too.',
    job: (n: number) => `Job ${n}`,
    remove: 'Remove',
    company: 'Company or farm',
    role: 'What was your job title?',
    rolePlaceholder: 'Example: Harvest worker',
    startYear: 'Year you started',
    endYear: 'Year you left',
    current: 'I still work there',
    duties: 'What did you do on a normal day?',
    dutiesInvitation:
      "Tap the mic and tell it like you'd tell a friend: what you did from the moment you arrived, which machines you ran, how big the place was.",
    dutiesPlaceholder: 'I got there at 5 and picked coffee on the upper part of the farm…',
    addFirst: 'Add a job',
    addAnother: 'Add another job',
    noJobs:
      "If you've never had a formal job, you can keep going without filling anything in. What you know how to do goes in the next step.",
  },

  skills: {
    skills: 'Machines, tools and skills',
    skillsInvitation:
      'Say them all in one go into the mic (brush cutter, forklift, welding) and we split them up for you.',
    skillsPlaceholder: 'Brush cutter, forklift, a little welding',
    addToList: 'Add to the list',
    removeSkill: (skill: string) => `Remove ${skill}`,
    languages: 'Languages',
    languagePlaceholder: 'English',
    levelOf: (language: string) => `${language || 'Language'} level`,
    removeLanguage: (n: number) => `Remove language ${n}`,
    addLanguage: 'Add language',
    education: 'How far did you go in school?',
    educationInvitation:
      'Tell us out loud: the last grade you finished, any course or technical program you took, where and in what.',
    educationPlaceholder: 'I finished high school and took a refrigeration course at SENA',
  },

  availability: {
    relocate: 'Can you move to the United States for the season?',
    relocateYes: 'Yes, I can move',
    relocateNo: 'Not for now',
    startWhen: 'When can you start?',
    otherDate: 'Another date',
    otherDateLabel: 'Another start date',
    otherDatePlaceholder: 'Example: after the April harvest',
    physical: 'Physical ability',
    physicalIntro:
      "These jobs are physically demanding and employers always ask. Mark what applies and tell us the rest.",
    physicalInvitation:
      'Anything else they should know? Say it into the mic: how much weight you carried, how many hours straight, in what weather.',
    physicalPlaceholder: 'I carried 110 lb sacks all morning, I handle strong sun fine',
  },

  voice: {
    transcribing: 'Turning your voice into text',
    stop: 'Stop recording',
    record: 'Record my answer',
    addMore: 'Tap to add something else',
    tapAndTalk: 'Tap and talk',
    secondsLeft: (n: number) =>
      n === 1
        ? '1 second left. Wrap up the thought and keep going in another recording.'
        : `${n} seconds left. Wrap up the thought and keep going in another recording.`,
    timeUp: 'We saved what you recorded. You can keep going in another recording.',
    preferWriting: "I'd rather type",
    thinking: 'Listening to what you said…',
    coachHintMic: "Answer with the mic and it's added to what you already said.",
    coachHintText: "Type it above and it's added to what you already said.",
    continueAsIs: 'Keep it as is',
    charsLeft: (n: number) =>
      n > 0
        ? n === 1
          ? '1 character left in this answer.'
          : `${n} characters left in this answer.`
        : "You've reached the limit for this answer. Anything else can go in the next one.",
    recordingFailed: "We couldn't use your recording.",
    problem: {
      denied:
        "You didn't give us microphone access, and that's fine. Type it here: the more you tell us about what you did, the better your resume turns out.",
      unsupported: "This browser can't record. Type it here and we'll keep going.",
      failed: "We couldn't open the microphone. Try again, or type it here.",
      empty:
        "We couldn't hear you. Hold the phone closer and keep the button on until you finish, or type it here.",
    },
  },

  transcribeErrors: {
    audio_too_large: 'The audio came out too long. Record it in shorter pieces, or type it here.',
    empty_audio: "We couldn't hear you. Hold the phone closer and try again, or type it here.",
    unsupported_audio_type:
      "Your phone recorded in a format we can't read. Type it here and we'll keep going.",
    invalid_audio: 'Something went wrong with the recording. Try again, or type it here.',
    rate_limited: "You recorded a lot of times in a row. Type it here and we'll keep going.",
    transcription_failed: "We couldn't turn your voice into text right now. Type it here and we'll keep going.",
    unauthorized: 'Your session expired. Sign in again.',
    network: 'No connection. Check your internet, or type it here.',
    unknown: "We couldn't use your recording. Type it here and we'll keep going.",
  },

  apiErrors: {
    no_membership: "We're still finishing setting up your account. Try again.",
    invalid_screening: 'Check the highlighted fields and try again.',
    screening_failed: "We couldn't put your resume together. Try again in a moment.",
    invalid_request: "We couldn't send your change. Try again.",
    cv_not_found: "We couldn't find that resume. Fill out the questionnaire again.",
    not_a_screening_cv: "That resume was uploaded as a file, so we can't rewrite it.",
    missing_screening_answers: "We're missing your answers. Fill out the questionnaire again.",
    regeneration_limit:
      "We've already adjusted it three times. To change details, go back to the questionnaire.",
    set_primary_failed: "We couldn't switch your resume. Try again.",
    unauthorized: 'Your session expired. Sign in again.',
    network: 'No connection. Check your internet and try again.',
    unknown: 'Something went wrong on our end. Try again.',
  },

  page: {
    checkMarked: 'Check the highlighted fields and try again.',
    submitFailed: 'Something went wrong. Try again.',
    regenerateFailed: "We couldn't redo it.",
    prefilling: 'Bringing back what you already answered…',
  },

  review: {
    eyebrow: 'Your resume',
    title: "Here's how it turned out",
    lede: "It's in English because that's how employers in the United States read it. That's on purpose, not a mistake.",
    summary: 'Summary',
    skills: "Skills we'll use to find matching jobs",
    notPrimary:
      'We saved this resume, but your applications still go out with the one you already had. Which one do you want to apply with?',
    useThis: 'Use this resume',
    keepCurrent: 'Keep the one I had',
    confirm: 'Looks good, continue',
    improve: 'Improve it',
    adjustmentsLeft: (n: number) =>
      n === 1 ? 'You can ask for 1 more change.' : `You can ask for ${n} more changes.`,
    limitBefore: "We've already adjusted it three times.",
    limitLink: 'Change your answers',
    limitAfter: "if something's missing.",
    feedbackLabel: 'What do you want to sound different?',
    feedbackPlaceholder: 'Example: have the summary talk more about my harvest experience.',
    rewriteBefore: "We rewrite the text using what you already told us. To add new details,",
    rewriteLink: 'go back to the questionnaire',
    rewriteAfter: '.',
    cancel: 'Cancel',
    redo: 'Redo it',
    backToAnswers: 'Back to my answers',
    scoreScope: 'How strong it is',
    addMissing: 'Add this information',
    addMissingHint: "You'll go back to the questionnaire with everything you already answered.",
  },
  /** Las preguntas que viajan al coach con cada respuesta hablada (no se ven). */
  coachQuestions: {
    duties: 'What did you do on a normal workday?',
    skills: 'What machines and tools can you run?',
    education: 'How far did you go in school?',
    physical: 'How much physical effort can you handle in a workday?',
  },
};

export default screening;
